import React, { useEffect, useState } from "react";
import {
    Typography,
    Checkbox,
    FormControlLabel,
    Paper,
    Button,
    Grid,
    IconButton,
    Tooltip,
    Pagination,
    FormControl,
    Select,
    MenuItem,
    Box,
    TextField,
    Snackbar,
    Alert,
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';

import { unitsApiKeyStore } from "../../../../api/units-api-key-store";
import { useLoadData } from "../../../../hooks/useLoadData";
import { ApiResponse } from "../../../../models/api-response";
import { LoadingIndicator } from "../../../../components/loading-indicator.component";
import { useSaveData } from "../../../../hooks/useSaveData";
import { fileUploadApiKeyStore } from "../../../../api/file-upload-key-store";
import { useFileDownload } from "../../../../components/file-download";
import { UserPermissionName } from "../../../../models/user-permission-name";
import { GuardedComponent } from "../../../../components/guarded-component";
import { useSnackbar } from "../../../../hooks/use-snackbar.hook";
import { WorkorderDocumentDialog } from "../../../workorders/workorder-document/workorder-document.dialog";
import dayjs from "dayjs";

const downloadBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const handleFileFunctionality = (fileResponse: any, fileData: any) => {
    if (fileResponse) {
        if (typeof fileResponse === 'string') {
            const blob = new Blob([new Uint8Array(
                Array.from(atob(fileResponse), (char) => char.charCodeAt(0)))],
                { type: fileData?.fileType });
            downloadBlob(blob, fileData?.fileName);
        } else {
            throw new Error('Invalid data type for file download.');
        }
    }
}

interface InspectionReportsTab {
    unitID: any;
    customerName: string;
}

export const InspectionReportsTab = ({ unitID, customerName }: InspectionReportsTab) => {

    //   pagination
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [customFileName, setCustomFileName] = useState<any>();
    const [openWODocumentDialog, setOpenWODocumentDialog] = useState(false);
    const [workOrderDocumentData, setWOFileData] = useState<any>();

    const addTimeStampInFileName = () => {
        const fileName = `CombinedInspectionReport_${dayjs().format('MMDDYYYY_HHmmss')}`;
        setCustomFileName(fileName);
    }

    useEffect(() => {
        addTimeStampInFileName();
    }, [])

    const [selectedWorkOrder, setSelectedWorkOrder] = useState(new Set());
    const [selectedFiles, setSelectedFiles] = useState(new Set());
    const [selectAll, setSelectAll] = useState(false);
    const [inspectionReportData, setInspectionReport] = useState<any>();
    const [singleFile, setSingleFile] = useState<any>();

    const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

    const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(unitsApiKeyStore.getUnitInspectionReports(unitID));

    const { isLoading: isFileDoanloadLoading, mutateAsync } = useSaveData<any>(unitsApiKeyStore.downloadInspectionReports());

    useEffect(() => {
        if (data) {
            setInspectionReport(data?.data ?? []);
        }
    }, [data])

    const validInspectionReport = Array.isArray(inspectionReportData) ? inspectionReportData : [];

    const startIndex = (page - 1) * itemsPerPage;
    const paginatedInspectionReports = validInspectionReport.slice(startIndex, startIndex + itemsPerPage);

    const getKey = (workOrderID: number, fileID: number) => `${workOrderID}:${fileID}`;

    const actionSeletedAll = (updatedWorkOrder: any) => {
        const allWorkOrderIDs = inspectionReportData?.map((item: any) => item.workOrderID);
        const isAllSelected = allWorkOrderIDs?.every((id: any) => updatedWorkOrder.has(id));
        setSelectAll(isAllSelected);
    }

    const toggleWorkOrder = (workorderItem: any) => {
        const updatedWOSet = new Set(selectedWorkOrder);
        const updatedFileSet = new Set(selectedFiles);
        if (!updatedWOSet.has(workorderItem?.workOrderID)) {
            updatedWOSet.add(workorderItem?.workOrderID)
            workorderItem?.inspectionReports?.forEach((file: any) => updatedFileSet.add(getKey(workorderItem?.workOrderID, file?.fileID)))
        }
        else {
            updatedWOSet.delete(workorderItem?.workOrderID)
            workorderItem?.inspectionReports?.forEach((file: any) => updatedFileSet.delete(getKey(workorderItem?.workOrderID, file?.fileID)))
        }
        actionSeletedAll(updatedWOSet);
        setSelectedWorkOrder(updatedWOSet);
        setSelectedFiles(updatedFileSet);
        addTimeStampInFileName();

    }

    const getCompletedWorkOrders = (updatedFileSet: any) => {
        return inspectionReportData
            .filter((workOrder: any) =>
                workOrder?.inspectionReports.every((file: any) =>
                    updatedFileSet.has(getKey(workOrder?.workOrderID, file?.fileID))
                )
            )
            .map((workOrder: any) => workOrder.workOrderID);
    };

    const toggleFile = (workOrderID: number, fileItem: any) => {
        const key = getKey(workOrderID, fileItem?.fileID);
        const updatedSet = new Set(selectedFiles);
        if (updatedSet.has(key)) {
            updatedSet.delete(key);
        } else {
            updatedSet.add(key);
        }
        const workOrderIDs = getCompletedWorkOrders(updatedSet);
        actionSeletedAll(new Set(workOrderIDs));
        setSelectedWorkOrder(new Set(workOrderIDs));
        setSelectedFiles(updatedSet);
        addTimeStampInFileName();
    };

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectedWorkOrder(new Set());
            setSelectedFiles(new Set());
        } else {
            const updatedWOSet = new Set();
            const updatedFileSet = new Set();
            inspectionReportData.forEach((report: any) => {
                updatedWOSet?.add(report?.workOrderID);
                report?.inspectionReports?.forEach((file: any) => updatedFileSet.add(getKey(report?.workOrderID, file?.fileID)))
            });
            setSelectedWorkOrder(updatedWOSet);
            setSelectedFiles(updatedFileSet);
            addTimeStampInFileName();
        }
        setSelectAll(!selectAll);
    };

    const handleDownloadFile = async (workOrderID: number, fileData: any) => {
        const downloadSingleFile = {
            fileID: fileData?.fileID,
            workOrderID: workOrderID,
            fileSection: "workOrder",
            fileName: fileData?.fileName,
            fileType: 'application/pdf',
        }
        setSingleFile(downloadSingleFile);
        // showSnackbar(`Processing your download...`, 'warning');
        await downloadFile({ additionalData: downloadSingleFile });
    }

    const handleDownloadFiles = async () => {
        const fileIDs = Array.from(selectedFiles).map(key => {
            const [_, fileID] = (key as string).split(":");
            return Number(fileID);
        });


        const downloadReports: any[] = [];

        inspectionReportData?.forEach((reportGroup: any) => {
            reportGroup.inspectionReports?.forEach((report: any) => {
                if (fileIDs?.includes(report.fileID)) {
                    downloadReports.push({
                        fileURL: report?.fileURL ?? '',
                        inspectionCompletedDate: reportGroup?.inspectionCompletedDate ?? '',
                        uploadedOn: report?.uploadedOn ?? '',
                        workOrderFinalStatus: reportGroup?.workOrderFinalStatus ?? ''
                    });
                }
            });
        });
        // showSnackbar(`Processing your download...`, 'warning');
        const fileResponse = await mutateAsync({ data: downloadReports });
        handleFileFunctionality(fileResponse, {
            fileName: `${customFileName}.pdf`,
            fileType: 'application/pdf',
        });
        setSelectedWorkOrder(new Set());
        setSelectedFiles(new Set());
        setSelectAll(false);
        addTimeStampInFileName();
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
    };

    const handleItemsPerPageChange = (event: any) => {
        setItemsPerPage(event.target.value as number);
        setPage(1);
    };

    const handleFileName = (event: any) => {
        setCustomFileName(event?.target?.value ?? '')
    }

    const handleOpenWODocumentDialog = (worOrderFileData: any) => {
        setWOFileData(worOrderFileData);
        setOpenWODocumentDialog(true);
    }

    const handleClosedWODocumentDialog = () => {
        setWOFileData(undefined);
        setOpenWODocumentDialog(false);
    }

   
    return (
        <div>
            <Grid container spacing={1}>
                <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <Grid container spacing={1}>
                        <Grid item lg={5} sm={5} xl={5} xs={5}>
                            {paginatedInspectionReports?.length > 0 && (
                                <FormControlLabel
                                    control={
                                        <Checkbox checked={selectAll} onChange={toggleSelectAll} disabled={isLoading || isFetching} />
                                    }
                                    label="Select All"
                                    sx={{ mb: 2 }}
                                />
                            )}
                        </Grid>
                        <Grid item lg={7} sm={7} xl={7} xs={7}>
                            <Grid
                                container
                                direction="row"
                                sx={{
                                    justifyContent: "flex-end",
                                    alignItems: "flex-start",
                                }}
                            >
                                {selectedFiles?.size !== 0 && (
                                    <TextField
                                        id="outlined-basic"
                                        label="File Name"
                                        variant="outlined"
                                        size="small"
                                        value={customFileName}
                                        onChange={handleFileName}
                                        style={{ marginTop: 5, marginRight: 10, width: '300px' }}
                                    />
                                )}
                                <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                                    <Button style={{ height: '40px', marginTop: 5 }}
                                        size="small"
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadFiles}
                                        disabled={selectedFiles?.size === 0 || customFileName == ""}
                                    >
                                        Download ({selectedFiles?.size}) {selectedFiles?.size === 1 ? 'Report' : 'Reports'}
                                    </Button>
                                </GuardedComponent>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item lg={12} sm={12} xl={12} xs={12} style={{ marginLeft: 20 }}>
                    <LoadingIndicator isLoading={isLoading || isFetching || isFileDoanloadLoading} />
                    {paginatedInspectionReports.map((workorder) => (
                        <div key={`${workorder.workOrderID}-${workorder.workOrderNumber}`}>
                            <Grid container spacing={2}>
                                <Grid item lg={12} sm={12} xl={12} xs={12}>
                                    <Grid container spacing={0}>
                                        <Grid item lg={0.3} sm={0.3} xl={0.3} xs={0.3}>
                                            <Checkbox style={{ paddingTop: 30 }}
                                                checked={selectedWorkOrder.has(workorder?.workOrderID)}
                                                onChange={() => toggleWorkOrder(workorder)}
                                            />
                                        </Grid>
                                        <Grid item lg={11.7} sm={11.7} xl={11.7} xs={11.7}>
                                            <Paper variant="outlined" style={{ width: '100%', padding: '10px' }}>
                                                <Grid container spacing={1}>
                                                    <Grid item lg={2} sm={2} xl={2} xs={2}>
                                                        <Grid container spacing={1}>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" color="text.secondary">
                                                                    Work Order
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    {workorder?.workOrderNumber}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item lg={2} sm={2} xl={2} xs={2}>
                                                        <Grid container spacing={1}>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" color="text.secondary">
                                                                    Service
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    {workorder?.serviceName}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item lg={2} sm={2} xl={2} xs={2}>
                                                        <Grid container spacing={1}>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" color="text.secondary">
                                                                    Municipality Code
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    {workorder?.municipalityCode}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item lg={2} sm={2} xl={2} xs={2}>
                                                        <Grid container spacing={1}>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" color="text.secondary">
                                                                    Work Order Status
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    {workorder?.workOrderStatus}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item lg={2} sm={2} xl={2} xs={2}>
                                                        <Grid container spacing={1}>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" color="text.secondary">
                                                                    PPI Status
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    {workorder?.ppiStatus}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                    <Grid item lg={2} sm={2} xl={2} xs={2}>
                                                        <Grid container spacing={1}>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" color="text.secondary">
                                                                    Inspection Completed Date
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item lg={12} sm={12} xl={12} xs={12}>
                                                                <Typography variant="body1" fontWeight="bold">
                                                                    {workorder?.inspectionCompletedDate}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item lg={12} sm={12} xl={12} xs={12} style={{ marginLeft: 40 }}>
                                    {workorder?.inspectionReports.map((file: any) => (
                                        <Grid key={`${file?.fileID}-${file?.fileName}`} container spacing={0} style={{ paddingBottom: 13 }}>
                                            <Grid item lg={0.3} sm={0.3} xl={0.3} xs={0.3}>
                                                <Checkbox
                                                    checked={selectedFiles.has(`${workorder?.workOrderID}:${file?.fileID}`)}
                                                    onChange={() => toggleFile(workorder?.workOrderID, file)}
                                                />
                                            </Grid>
                                            <Grid item lg={11.7} sm={11.7} xl={11.7} xs={11.7}>
                                                <Paper variant="outlined" style={{ padding: '10px' }}>
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        sx={{
                                                            justifyContent: "space-between",
                                                            alignItems: "flex-start",
                                                        }}
                                                    >
                                                        <Typography variant="body2">{file?.fileName}</Typography>
                                                        {
                                                            (isFileDownloadLoading && singleFile?.fileID == file?.fileID) ?
                                                                <Box sx={{ display: 'flex' }}>
                                                                    <CircularProgress size="28px" />
                                                                </Box>
                                                                :
                                                                <div>
                                                                    <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_INSPECTION_REPORT]}>
                                                                        <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleOpenWODocumentDialog(file)}>
                                                                            <Tooltip title={<Typography variant="body1">Edit</Typography>}>
                                                                                <EditIcon className="btnEdit" />
                                                                            </Tooltip>
                                                                        </IconButton>
                                                                    </GuardedComponent>
                                                                    <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                                                                        <IconButton style={{ padding: 5 }} aria-label="view" onClick={() => handleDownloadFile(workorder?.workOrderID, file)}>
                                                                            <Tooltip title={<Typography variant="body1">Download</Typography>}>
                                                                                <DownloadIcon className="btnEdit" />
                                                                            </Tooltip>
                                                                        </IconButton>
                                                                    </GuardedComponent>
                                                                </div>
                                                        }
                                                    </Grid>
                                                </Paper>
                                            </Grid>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </div>
                    ))
                    }
                </Grid >
            </Grid >
            {
                validInspectionReport.length > 0 && (
                    <Grid
                        padding={10}
                        style={{
                            padding: '20px 0px 15px',
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                        }}
                    >
                        <Pagination
                            count={Math.ceil(validInspectionReport.length / itemsPerPage)}
                            page={page}
                            onChange={handlePageChange}
                            variant="outlined"
                            shape="rounded"
                        />
                        <FormControl className="pagingperSelect" variant="outlined">
                            <Select
                                labelId="items-per-page-label"
                                id="items-per-page-select"
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                            >
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={30}>30</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                            <span>items per page</span>
                        </FormControl>
                    </Grid>
                )
            }
            {openWODocumentDialog && (
                <WorkorderDocumentDialog
                    open={openWODocumentDialog}
                    documentRequestArgs={{
                        fileData: workOrderDocumentData,
                        workOrderID: Number(workOrderDocumentData?.workOrderID),
                        workOrderNumber: workOrderDocumentData?.workOrderNumber
                    }}
                    parentRefetch={refetch}
                    onClose={handleClosedWODocumentDialog}
                />
            )}
            {isFileDoanloadLoading && (
                <Snackbar
                    open={isFileDoanloadLoading}
                    // onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    style={{ zIndex: 10000000 }}
                >
                    <div>
                        <Alert severity={'warning'} variant="filled" sx={{ width: '100%' }}>
                            Processing your download...
                        </Alert>
                    </div>
                </Snackbar>
            )}
        </div>
    );
}
